import {
  PivotDataSource,
  PivotDataSourceMode,
  PivotRefreshState,
  PivotRollupReason,
} from "../../types/pivot.type";

/**
 * How the page reads `data_source` — the field every pivot response carries saying which
 * path answered it.
 *
 * The whole point of the banner is that a slow pivot is not a broken one. `LIVE` means the
 * precomputed daily rollup was not used and the aggregation ran against the fact rows
 * instead: the numbers are correct and current, the wait was just long. So every visible
 * message ends by saying the figures are up to date — "the rollup was not used" reads as a
 * data fault to anyone who has never heard of the rollup.
 */

const DATA_SOURCE_MODES: PivotDataSourceMode[] = ["ROLLUP", "HYBRID", "LIVE"];

const ROLLUP_REASONS: PivotRollupReason[] = [
  "STALE",
  "COVERAGE_GAP",
  "COVERAGE_TOO_SHORT",
  "WINDOW_IS_TODAY",
  "DISABLED",
  "TIMEZONE_MISMATCH",
  "NO_BRANCHES_IN_SCOPE",
  "UNAVAILABLE",
];

const REFRESH_STATES: PivotRefreshState[] = [
  "STARTED",
  "ALREADY_RUNNING",
  "COOLING_DOWN",
  "DISABLED",
  "NOT_REQUESTED",
];

export type PivotNoticeSeverity = "info" | "warning";

interface ReasonCopy {
  /** `null` keeps the page deliberately silent — see the two permanent-and-normal cases. */
  message: string | null;
  severity: PivotNoticeSeverity;
}

/**
 * Thai copy keyed on `reason`, which is the stable code; `data_source.message` is English
 * prose and only stands in for a code this build has never seen.
 *
 * `WINDOW_IS_TODAY` and `DISABLED` are silent on purpose. Today is always read live by
 * design and the flag is an environment setting — both are permanent and normal, and a
 * banner on every "today" view would train people to ignore all of them, including the ones
 * that mean something. `TIMEZONE_MISMATCH` is silent too: this page never sends a timezone,
 * so it cannot happen from here and there is nothing the user could do about it.
 */
export const PIVOT_ROLLUP_REASON_COPY: Record<PivotRollupReason, ReasonCopy> = {
  STALE: {
    message: "กำลังปรับปรุงข้อมูลสรุป — ตัวเลขที่แสดงเป็นข้อมูลล่าสุด",
    severity: "info",
  },
  COVERAGE_GAP: {
    message: "กำลังสร้างข้อมูลสรุปของสาขา — ตัวเลขที่แสดงเป็นข้อมูลล่าสุด",
    severity: "info",
  },
  COVERAGE_TOO_SHORT: {
    message:
      "ช่วงวันที่ย้อนหลังเกินข้อมูลสรุปที่มี — ตัวเลขที่แสดงเป็นข้อมูลล่าสุด แต่จะโหลดช้ากว่าปกติ",
    severity: "warning",
  },
  WINDOW_IS_TODAY: { message: null, severity: "info" },
  DISABLED: { message: null, severity: "info" },
  TIMEZONE_MISMATCH: { message: null, severity: "info" },
  NO_BRANCHES_IN_SCOPE: {
    message: "ไม่พบสาขาในขอบเขตที่เลือก",
    severity: "warning",
  },
  UNAVAILABLE: {
    message: "ตัวเลขที่แสดงเป็นข้อมูลล่าสุด แต่ช่วงนี้จะโหลดช้ากว่าปกติ",
    severity: "info",
  },
};

/**
 * The banner to show for this response, or `null` for silence.
 *
 * The one invariant worth coding against: `reason` is non-null exactly when the mode is
 * `LIVE`. A `HYBRID` response is the normal shape whenever the range includes today — its
 * `live_from` says where the live half picked up, and is not a fault to report.
 */
export const pivotDataSourceNotice = (
  dataSource: PivotDataSource | null | undefined,
): { message: string; severity: PivotNoticeSeverity } | null => {
  if (!dataSource || dataSource.mode !== "LIVE") return null;

  const copy = dataSource.reason ? PIVOT_ROLLUP_REASON_COPY[dataSource.reason] : undefined;
  // An unknown reason code means the backend grew one this build has not been taught. Fall
  // back to its own English message rather than going silent about a slow path.
  if (!copy) {
    return dataSource.message
      ? { message: dataSource.message, severity: "info" }
      : null;
  }

  return copy.message ? { message: copy.message, severity: copy.severity } : null;
};

/**
 * Whether it is worth polling `/pivot/rollup/status` after this response.
 *
 * `STARTED` does not promise a rebuild is running — it means this API task asked for one,
 * and a cluster-wide lock may have deflected it onto a run already in progress. Either way
 * something is expected to be running, and only the status endpoint knows the truth, so
 * both states start the same watch.
 */
export const shouldWatchRollupRebuild = (
  dataSource: PivotDataSource | null | undefined,
): boolean =>
  dataSource?.mode === "LIVE" &&
  (dataSource.refresh?.state === "STARTED" ||
    dataSource.refresh?.state === "ALREADY_RUNNING");

/** "ประมาณ 4 นาที" / "ประมาณ 45 วินาที". `null` while nothing is running. */
export const formatRollupEta = (seconds: number | null | undefined) => {
  if (seconds === null || seconds === undefined || seconds < 0) return null;
  if (seconds < 90) return `ประมาณ ${Math.max(1, Math.round(seconds))} วินาที`;
  return `ประมาณ ${Math.round(seconds / 60)} นาที`;
};

// ---- CSV export headers -------------------------------------------------------

type HeaderBag =
  | Record<string, unknown>
  | { get: (name: string) => string | null | undefined };

const readHeader = (headers: HeaderBag | undefined, name: string) => {
  if (!headers) return undefined;

  const getter = (headers as { get?: unknown }).get;
  if (typeof getter === "function") {
    const value = (getter as (n: string) => unknown).call(headers, name);
    return typeof value === "string" && value !== "" ? value : undefined;
  }

  const bag = headers as Record<string, unknown>;
  const value = bag[name.toLowerCase()] ?? bag[name];
  return typeof value === "string" && value !== "" ? value : undefined;
};

/**
 * `GET /pivot/export` answers with a CSV body and no JSON envelope, so the same verdict
 * comes back as `X-Pivot-*` response headers — all of them already in the API's CORS
 * `exposedHeaders`, so they survive the cross-origin read.
 *
 * Null fields are *omitted*, never sent empty, which is why every optional one is read as
 * presence: an empty `reason` would say "refused, no code given" rather than "not refused".
 * `X-Pivot-Rollup-Message` is dropped when it is non-ASCII, so it can be missing even on a
 * response that has a reason — one more thing the reason code, not the prose, has to carry.
 */
export const readPivotExportDataSource = (
  headers: HeaderBag | undefined,
): PivotDataSource | null => {
  const mode = readHeader(headers, "x-pivot-data-source");
  if (!mode || !DATA_SOURCE_MODES.includes(mode as PivotDataSourceMode)) return null;

  const reason = readHeader(headers, "x-pivot-rollup-reason");
  const state = readHeader(headers, "x-pivot-rollup-refresh");
  const retryAfter = readHeader(headers, "x-pivot-rollup-retry-after");
  const parsedRetry = retryAfter === undefined ? NaN : Number(retryAfter);

  return {
    mode: mode as PivotDataSourceMode,
    reason:
      reason && ROLLUP_REASONS.includes(reason as PivotRollupReason)
        ? (reason as PivotRollupReason)
        : null,
    message: readHeader(headers, "x-pivot-rollup-message") ?? null,
    live_from: readHeader(headers, "x-pivot-live-from") ?? null,
    refreshed_at: readHeader(headers, "x-pivot-rollup-refreshed-at") ?? null,
    refresh: {
      state:
        state && REFRESH_STATES.includes(state as PivotRefreshState)
          ? (state as PivotRefreshState)
          : "NOT_REQUESTED",
      retry_after_seconds: Number.isFinite(parsedRetry) ? parsedRetry : null,
    },
  };
};
